import { Component, inject, input, OnInit, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BrokerService } from '@app/core/services/broker.service';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  RentalAgreementFilter,
  RentalAgreementStatus,
  RentalAgreementStatusesDisplayNames
} from '../rental-agreements.model';
import { MultiSelectModule } from 'primeng/multiselect';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DatePickerModule } from 'primeng/datepicker';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BrokerAutocompleteDirective } from '@app/shared/directives/broker-autocomplete.directive';
import { CheckboxModule } from 'primeng/checkbox';
import { LocationMultiselectDirective } from '@app/shared/directives/location-multiselect.directive';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '@app/core/services/auth.service';
import { SelectModule } from 'primeng/select';

type SearchPredicateType = 'reservationNumber' | 'rentalAgreementNumber' | 'reference';

interface RentalAgreementFilterForm {
  searchText: FormControl<string | null>;
  predicate: FormControl<SearchPredicateType | null>;
  driverFirstName: FormControl<string | null>;
  driverLastName: FormControl<string | null>;
  clientId: FormControl<{ code: number; selectedLabel: string } | null>;
  pickupLocations: FormControl<{ code: string }[] | null>;
  dropOffLocations: FormControl<{ code: string }[] | null>;
  dateOutRange: FormControl<(Date | null)[] | null>;
  dateInRange: FormControl<(Date | null)[] | null>;
  creationDateRange: FormControl<(Date | null)[] | null>;
  statuses: FormControl<RentalAgreementStatus[] | null>;
}

@Component({
  selector: 'app-rental-agreement-filter',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgbTypeaheadModule,
    MultiSelectModule,
    IftaLabelModule,
    DatePickerModule,
    AutoCompleteModule,
    BrokerAutocompleteDirective,
    CheckboxModule,
    LocationMultiselectDirective,
    ButtonModule,
    SelectModule
  ],
  templateUrl: './rental-agreement-filter.component.html',
  styleUrl: './rental-agreement-filter.component.scss',
  animations: [
    trigger('advancedFilterAnimation', [
      transition(':enter', [
        style({ opacity: 0, height: '0' }),
        animate('200ms ease-out', style({ opacity: 1, height: '*' }))
      ]),
      transition(':leave', [animate('200ms ease-out', style({ height: '0', opacity: 0 }))])
    ]),
    trigger('searchBtnAnimation', [
      transition(':leave', [animate('200ms ease-out', style({ width: '0', opacity: 0 }))]),
      transition(':enter', [
        style({ opacity: 0, width: '0' }),
        animate('200ms ease-out', style({ width: '*', opacity: 1 }))
      ])
    ])
  ]
})
export class RentalAgreementFilterComponent implements OnInit {
  private _brokerService = inject(BrokerService);
  private _authService = inject(AuthService);

  initialState = input<RentalAgreementFilter | null>(null);
  onFilterChange = output<RentalAgreementFilter>();

  advancedFilter = false;
  now = new Date();
  form: FormGroup<RentalAgreementFilterForm>;

  constructor() {
    this.form = new FormGroup<RentalAgreementFilterForm>({
      searchText: new FormControl(null),
      predicate: new FormControl('rentalAgreementNumber'),
      driverFirstName: new FormControl(null),
      driverLastName: new FormControl(null),
      clientId: new FormControl(null),
      pickupLocations: new FormControl(null),
      dropOffLocations: new FormControl(null),
      dateOutRange: new FormControl(null),
      dateInRange: new FormControl(null),
      creationDateRange: new FormControl(null),
      statuses: new FormControl(null)
    });
  }

  ngOnInit(): void {
    const initValues = this.initialState();

    if (!initValues) {
      return;
    }

    const { rentalAgreementNumber, reservationNumber, reference, ...filterValues } = initValues;

    if (Object.keys(filterValues).length > 0) {
      this.advancedFilter = true;
    }

    const statuses = initValues.statuses
      ? initValues.statuses instanceof Array
        ? initValues.statuses.map((x) => Number(x) as RentalAgreementStatus)
        : [Number(initValues.statuses) as RentalAgreementStatus]
      : [];

    const pickupLocations = initValues.pickupLocations
      ? initValues.pickupLocations.split(',').map((x) => ({ code: x }))
      : null;

    const dropOffLocations = initValues.dropOffLocations
      ? initValues.dropOffLocations.split(',').map((x) => ({ code: x }))
      : null;

    const initFormValues = {
      predicate: 'rentalAgreementNumber' as SearchPredicateType,
      searchText: rentalAgreementNumber ?? null,
      driverFirstName: initValues.driverFirstName ?? null,
      driverLastName: initValues.driverLastName ?? null,
      clientId: null,
      pickupLocations: pickupLocations,
      dropOffLocations: dropOffLocations,
      dateOutRange: this.buildDateRange(initValues.dateOutFrom, initValues.dateOutTo),
      dateInRange: this.buildDateRange(initValues.dateInFrom, initValues.dateInTo),
      creationDateRange: this.buildDateRange(initValues.creationDateFrom, initValues.creationDateTo),
      statuses: statuses
    };

    if (!isNaN(Number(initValues.clientId))) {
      this._brokerService.getBrokerByCode(initValues.clientId!).subscribe((loc) => {
        this.form.controls.clientId.setValue({
          code: loc.code,
          selectedLabel: `${loc.code} - ${loc.name}`
        });
      });
    }

    if (reservationNumber) {
      initFormValues.searchText = reservationNumber;
      initFormValues.predicate = 'reservationNumber';
    } else if (reference) {
      initFormValues.searchText = reference;
      initFormValues.predicate = 'reference';
    }

    this.form.setValue(initFormValues);
    this.form.markAsDirty();
  }

  submitSearch(): void {
    const { searchText, predicate } = this.form.getRawValue();

    if (!predicate) {
      return;
    }

    const filterValues = {} as RentalAgreementFilter;

    filterValues[predicate!] = searchText;

    this.onFilterChange.emit(filterValues);
  }

  submitFilter(): void {
    const {
      predicate,
      searchText,
      creationDateRange,
      dateOutRange,
      dateInRange,
      clientId,
      pickupLocations,
      dropOffLocations,
      ...formValues
    } = this.form.getRawValue();

    const filterValues: RentalAgreementFilter = {
      ...formValues,
      creationDateFrom: this.formatDate(creationDateRange?.[0]),
      creationDateTo: this.formatDate(creationDateRange?.[1]),
      dateOutFrom: this.formatDate(dateOutRange?.[0]),
      dateOutTo: this.formatDate(dateOutRange?.[1]),
      dateInFrom: this.formatDate(dateInRange?.[0]),
      dateInTo: this.formatDate(dateInRange?.[1]),
      clientId: clientId?.code,
      pickupLocations: pickupLocations?.map((x) => x.code).join(','),
      dropOffLocations: dropOffLocations?.map((x) => x.code).join(',')
    };

    filterValues[predicate!] = searchText;

    this.onFilterChange.emit(filterValues);
  }

  reset(): void {
    this.form.reset({ predicate: 'rentalAgreementNumber' });
    this.onFilterChange.emit({});
  }

  expandFilter(): void {
    this.advancedFilter = true;
  }

  collapseFilter(): void {
    this.advancedFilter = false;
  }

  getStatusesOptions(): { label: string; value: RentalAgreementStatus }[] {
    return Object.values(RentalAgreementStatus)
      .filter((x) => !isNaN(Number(x)))
      .map((status) => ({
        label: RentalAgreementStatusesDisplayNames[status as RentalAgreementStatus],
        value: status as RentalAgreementStatus
      }));
  }

  getVisibleCountry(): string | null {
    return this._authService.IsFranchiseeUser ? this._authService.Country! : null;
  }

  private formatDate(date: Date | null | undefined): string | null {
    if (!date) {
      return null;
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildDateRange(dateFrom: string | null | undefined, dateTo: string | null | undefined): Date[] | null {
    if (!dateFrom && !dateTo) {
      return null;
    }
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    return from && to ? [from, to] : from ? [from] : [];
  }
}
